import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { keyframes } from "@mui/system";

// אנימציית הגלים – נגלול רקע (SVG) מימין לשמאל
const waveAnimation = keyframes`
  0% { background-position-x: 0; }
  100% { background-position-x: 1000px; }
`;

// אנימציה שנייה לגל נוסף
const waveAnimation2 = keyframes`
  0% { background-position-x: 0; }
  100% { background-position-x: 2000px; }
`;

const Login = () => {
  // State לניהול מייל, סיסמה ושגיאות
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  // state לניהול תצוגת הדיאלוגים
  const [forgotOpen, setForgotOpen] = useState(false);
  const [noAccountOpen, setNoAccountOpen] = useState(false);

  // טיפול באירוע התחברות
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // אם התחבר בהצלחה - מעבר למערכת הראשית
    } catch (err) {
      setError("שם משתמש או סיסמה לא נכונים");
    }
  };

  return (
    <Box
      sx={{
        // תופס את כל גובה ורוחב המסך
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        backgroundColor: "#121212",
        position: "relative"
      }}
    >
      {/* 1. אזור הגלים – צד שמאל (מוסתר במסכים קטנים) */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "block" },
          position: "relative"
        }}
      >
        {/* גל ראשון – עם אנימציית גלילה */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            backgroundImage: `url('https://svgshare.com/i/ucx.svg')`,
            backgroundSize: "cover",
            backgroundRepeat: "repeat-x",
            backgroundPosition: "0 bottom",
            animation: `${waveAnimation} 30s linear infinite`,
            opacity: 0.3
          }}
        />
        {/* גל שני – שכבה נוספת ליצירת עומק */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            backgroundImage: `url('https://svgshare.com/i/ucx.svg')`,
            backgroundSize: "contain",
            backgroundRepeat: "repeat-x",
            backgroundPosition: "0 bottom",
            animation: `${waveAnimation2} 60s linear infinite`,
            opacity: 0.15
          }}
        />
        {/* כיתוב במרכז הצד השמאלי */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: "#fff",
            zIndex: 2
          }}
        >
          {/* תמונת הלוגו בעיגול */}
          <Box
            component="img"
            src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRu6EZo8CSDLYwi4uIzCQUwW4l7Ky0bdP7ypg&s"
            alt="Logo"
            sx={{
              width: 300,
              height: 300,
              borderRadius: "50%",
              objectFit: "cover",
              mb: 2,
              boxShadow: "0 0 10px rgba(0,0,0,0.5)"
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              textShadow: "0 0 10px rgba(0,0,0,0.7)"
            }}
          >
            ברוכים הבאים
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mt: 1,
              color: "#f0f0f0",
              textShadow: "0 0 5px rgba(0,0,0,0.5)"
            }}
          >
            לפלטפורמת הבק-טסטינג לפיתוח אסטרטגיות
          </Typography>
        </Box>
      </Box>

      {/* 2. אזור הטופס – צד ימין (או מסך מלא במובייל) */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, md: 5 },
          position: "relative",
          // רקע גיבוי כהה
          backgroundColor: { xs: "#121212", md: "transparent" }
        }}
      >
        {/* קופסה עם אפקט זכוכית (Glassmorphism) */}
        <Box
          sx={{
            width: { xs: "100%", sm: "400px" },
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            boxShadow: "0 0 20px rgba(0,0,0,0.3)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            p: 4
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{
              mb: 2,
              color: "#fff",
              fontWeight: "bold",
              textShadow: "0 0 5px rgba(255,255,255,0.3)"
            }}
          >
            התחברות
          </Typography>
          {/* טופס ההתחברות */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="מייל"
              variant="filled"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                label: { color: "#aaa" },
                input: { color: "#fff" },
                "& .MuiFilledInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)"
                }
              }}
            />
            <TextField
              label="סיסמה"
              variant="filled"
              fullWidth
              margin="normal"
              type={showPass ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                label: { color: "#aaa" },
                input: { color: "#fff" },
                "& .MuiFilledInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)"
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPass(!showPass)}
                      edge="end"
                      sx={{ color: "#ccc" }}
                    >
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {/* הודעת שגיאה */}
            {error && (
              <Typography
                sx={{
                  color: "red",
                  mt: 1,
                  fontWeight: "bold",
                  textAlign: "center"
                }}
              >
                {error}
              </Typography>
            )}

            {/* אפשרות ל-"זכור אותי" */}
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    color: "#bbb",
                    "&.Mui-checked": {
                      color: "#6E07F3"
                    }
                  }}
                />
              }
              label={<Typography sx={{ color: "#ccc" }}>זכור אותי</Typography>}
              sx={{ mt: 1 }}
            />

            {/* כפתור התחברות */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: "1rem",
                borderRadius: 2,
                background:
                  "linear-gradient(45deg, #6E07F3, #3804A3 80%)",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.02)",
                  background: "linear-gradient(45deg, #7E24F3, #4A1CA3 80%)"
                }
              }}
            >
              התחבר
            </Button>

            {/* שכבת לינקים */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 2
              }}
            >
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setForgotOpen(true);
                }}
                underline="hover"
                sx={{ color: "#ccc", fontSize: "0.9rem" }}
              >
                ?שכחת סיסמה
              </Link>
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setNoAccountOpen(true);
                }}
                underline="hover"
                sx={{ color: "#ccc", fontSize: "0.9rem" }}
              >
                ?עדיין אין לך חשבון
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* דיאלוג לשכחת סיסמה */}
      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)}>
        <DialogTitle>?שכחת סיסמה</DialogTitle>
        <DialogContent>
          <Typography>
            ברור ששכחת, תפסיק לעשות סמים אח
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotOpen(false)}>סגור</Button>
        </DialogActions>
      </Dialog>

      {/* דיאלוג לאין חשבון */}
      <Dialog open={noAccountOpen} onClose={() => setNoAccountOpen(false)}>
        <DialogTitle>?אין לך חשבון</DialogTitle>
        <DialogContent>
          <Typography>
            אם אין לך חשבון סימן שאתה לא שייך לפה אח יקר
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoAccountOpen(false)}>סגור</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
